import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import NetworkSelector from '../components/NetworkSelector';
import CurrencySelector from '../components/CurrencySelector';
import AddressDisplay from '../components/AddressDisplay';
import LoadingSpinner from '../components/LoadingSpinner';
import Header from '../components/Header';
import ErrorMessage from '../components/ErrorMessage';

interface SupportedCurrency {
    network: string;
    currency: string;
    min_deposit: number;
    max_deposit: number;
    is_enabled: boolean;
    icon_url?: string;
    provider_name: string;
}

interface AddressData {
    network: string;
    currency: string;
    wallet_address: string;
    limits: {
        min: number;
        max: number;
    };
}

interface Card {
    id: number;
    holder_name: string;
    last4: string;
    createdAt: string;
}

const DedicatedAddressPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [supportedCurrencies, setSupportedCurrencies] = useState<SupportedCurrency[]>([]);
    const [selectedNetwork, setSelectedNetwork] = useState<string>('');
    const [selectedCurrency, setSelectedCurrency] = useState<string>('');
    const [addressData, setAddressData] = useState<AddressData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingCurrencies, setLoadingCurrencies] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [networks, setNetworks] = useState<string[]>([]);
    const [currenciesByNetwork, setCurrenciesByNetwork] = useState<Record<string, SupportedCurrency[]>>({});
    const [defaultCard, setDefaultCard] = useState<Card | null>(null);

    useEffect(() => {
        if (!token) {
            setError('Access token is required');
            return;
        }
        loadSupportedCurrencies();
        loadDefaultCard();
    }, [token]); // Only run when token changes

    const loadDefaultCard = async () => {
        if (!token) return;

        try {
            const response = await apiService.hasSelectedCard(token);
            if (response.card_details) {
                setDefaultCard(response.card_details);
            }
        } catch (err: any) {
            // Don't show error for card loading, it's not critical
        }
    };

    const loadSupportedCurrencies = async () => {
        if (!token || loadingCurrencies) return; // Prevent duplicate calls

        try {
            setLoadingCurrencies(true);
            setError(''); // Clear any previous errors
            const currencies = await apiService.getSupportedCurrencies(token);

            // Validate the response
            if (!Array.isArray(currencies)) {
                throw new Error('Invalid response format: expected array of currencies');
            }

            setSupportedCurrencies(currencies);

            // Group currencies by network
            const uniqueNetworks = Array.from(new Set(currencies.map((c: SupportedCurrency) => c.network)));
            setNetworks(uniqueNetworks);

            const grouped = uniqueNetworks.reduce((acc, network) => {
                acc[network] = currencies.filter(c => c.network === network);
                return acc;
            }, {} as Record<string, SupportedCurrency[]>);

            setCurrenciesByNetwork(grouped);
        } catch (err: any) {
            setError(err.message || 'Failed to load supported currencies');
        } finally {
            setLoadingCurrencies(false);
        }
    };

    const handleNetworkChange = (network: string) => {
        setSelectedNetwork(network);
        setSelectedCurrency('');
        setAddressData(null);
    };

    const handleCurrencyChange = (currency: string) => {
        setSelectedCurrency(currency);
        setAddressData(null);
    };

    const loadAddress = async () => {
        if (!selectedNetwork || !selectedCurrency || !token) return;

        try {
            setLoading(true);
            setError('');
            const data = await apiService.getUserAddress(token, selectedNetwork, selectedCurrency);
            setAddressData(data);
        } catch (err: any) {
            setError(err.message || 'Failed to load address');
        } finally {
            setLoading(false);
        }
    };

    const generateNewAddress = async () => {
        if (!selectedNetwork || !selectedCurrency || !token) return;

        try {
            setLoading(true);
            setError('');
            const data = await apiService.generateAddress(token, selectedNetwork, selectedCurrency);
            setAddressData(data);
        } catch (err: any) {
            setError(err.message || 'Failed to generate address');
        } finally {
            setLoading(false);
        }
    };

    const updateAddress = async (newAddress: string) => {
        if (!selectedNetwork || !selectedCurrency || !token) return;

        try {
            setLoading(true);
            setError('');
            const data = await apiService.updateAddress(token, selectedNetwork, selectedCurrency, newAddress);
            setAddressData(data);
        } catch (err: any) {
            setError(err.message || 'Failed to update address');
        } finally {
            setLoading(false);
        }
    };

    const handleChangeCard = () => {
        // Navigate to card selection page with changeCard parameter
        window.location.href = `/?token=${encodeURIComponent(token || '')}&changeCard=true`;
    };

    const handleBackToApp = () => {
        // Try to close the webview or navigate back to the mobile app
        if (window.history.length > 1) {
            window.history.back();
        } else {
            // Fallback: try to close the window/tab
            window.location.href = 'hexacard://callback?action=DISSMISS_APPLICATION_WEBVIEW';
            window.close();
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">

                <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
                    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
                            <p className="text-gray-600">A valid access token is required to access this page.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">

            <div className="container mx-auto px-4 py-6 max-w-4xl">

                {/* Network and Currency Selection */}
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-100">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                        </div>
                        Select Network & Currency
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <NetworkSelector
                            networks={networks}
                            selectedNetwork={selectedNetwork}
                            onNetworkChange={handleNetworkChange}
                            loading={loadingCurrencies}
                        />

                        <CurrencySelector
                            currencies={currenciesByNetwork[selectedNetwork] || []}
                            selectedCurrency={selectedCurrency}
                            onCurrencyChange={handleCurrencyChange}
                            disabled={!selectedNetwork || loadingCurrencies}
                            loading={loadingCurrencies}
                        />
                    </div>

                    <button
                        onClick={loadAddress}
                        disabled={!selectedNetwork || !selectedCurrency || loading}
                        className="w-full md:w-auto text-white px-8 py-3 rounded-xl hover:opacity-90 focus:outline-none focus:ring-4 focus:ring-blue-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg transition-all duration-200 transform hover:scale-105 disabled:transform-none"
                        style={{ backgroundColor: '#1d4df0' }}
                    >
                        {loading ? (
                            <div className="flex items-center justify-center">
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                Loading Address...
                            </div>
                        ) : (
                            'Load Address'
                        )}
                    </button>
                </div>

                {/* Default Card Display */}
                {defaultCard && (
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-100">
                        <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                            </div>
                            Default Deposit Card
                        </h2>

                        <div className="p-4 border-2 border-blue-100 bg-blue-50 rounded-xl mb-4">
                            <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center flex-1 min-w-0">
                                    <div className="w-12 h-8 bg-[#1d4df0] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                                        <svg className="w-8 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                        </svg>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-gray-900 truncate">{defaultCard.holder_name}</p>
                                        <p className="text-xs text-gray-600">**** {defaultCard.last4}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleChangeCard}
                                    className="px-4 py-2 text-white rounded-lg hover:opacity-90 focus:outline-none focus:ring-4 focus:ring-blue-200 font-medium text-sm shadow-md transition-all duration-200 flex-shrink-0"
                                    style={{ backgroundColor: '#1d4df0' }}
                                >
                                    Change Card
                                </button>
                            </div>
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                            <p className="text-sm text-blue-800">
                                <span className="font-semibold">Note:</span> All deposits to your dedicated wallet address will be credited to this card by default.
                            </p>
                        </div>
                    </div>
                )}

                {/* Loading Indicator */}
                {(loading || loadingCurrencies) && <LoadingSpinner />}

                {/* Error Message */}
                {error && <ErrorMessage message={error} onClose={() => setError('')} />}

                {/* Address Display */}
                {addressData && (
                    <AddressDisplay
                        addressData={addressData}
                        onGenerateNew={generateNewAddress}
                        onUpdateAddress={updateAddress}
                        loading={loading}
                    />
                )}
            </div>
        </div>
    );
};

export default DedicatedAddressPage;